import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, List, ListItem, ListItemText, TextField, Typography } from "@mui/material";

import {
  PasswordValidation,
  PWREQ_LENGTH,
  PWREQ_NUM_DIGIT,
  PWREQ_NUM_LOWER,
  PWREQ_NUM_SPECIAL,
  PWREQ_NUM_UPPER,
  SetSnackType,
} from "../../utils";

export const validatePassword = (pw: string): { fail: boolean; reason: PasswordValidation; err: string } => {
  const reason: PasswordValidation = {
    len: true,
    lower: true,
    upper: true,
    digit: true,
    special: true,
  };
  let err = "Invalid password";

  if (pw.length >= PWREQ_LENGTH) reason.len = false;
  if ((pw.match(/[a-z]/g) || []).length >= PWREQ_NUM_LOWER) reason.lower = false;
  if ((pw.match(/[A-Z]/g) || []).length >= PWREQ_NUM_UPPER) reason.upper = false;
  if ((pw.match(/\d/g) || []).length >= PWREQ_NUM_DIGIT) reason.digit = false;
  if ((pw.match(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~\u00C0-\u017F]/g) || []).length >= PWREQ_NUM_SPECIAL)
    reason.special = false;

  // reverse order for rendered requirements below
  if (reason.special) err = `Password needs at least ${PWREQ_NUM_SPECIAL} special characters`;
  if (reason.digit) err = `Password needs at least ${PWREQ_NUM_DIGIT}  characters`;
  if (reason.upper) err = `Password needs at least ${PWREQ_NUM_UPPER} capitals`;
  if (reason.lower) err = `Password needs at least ${PWREQ_NUM_LOWER} lowercase characters`;
  if (reason.len) err = `Password needs to be at least ${PWREQ_LENGTH} characters in length`;

  return { fail: reason.len || reason.lower || reason.upper || reason.digit || reason.special, reason, err };
};

export const Password = (props: {
  pw: string;
  pwErr: boolean;
  setPw: Dispatch<SetStateAction<string>>;
  setPwErr: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  submit: () => Promise<void>;
  withReqs?: boolean;
}) => {
  const [pwReqLen, setPwReqLen] = useState(false);
  const [pwReqLower, setPwReqLower] = useState(false);
  const [pwReqUpper, setPwReqUpper] = useState(false);
  const [pwReqDigit, setPwReqDigit] = useState(false);
  const [pwReqSpecial, setPwReqSpecial] = useState(false);

  useEffect(() => {
    props.setPwErr(false);
    const { reason } = validatePassword(props.pw);
    setPwReqLen(!reason.len);
    setPwReqLower(!reason.lower);
    setPwReqUpper(!reason.upper);
    setPwReqDigit(!reason.digit);
    setPwReqSpecial(!reason.special);
  }, [props.pw]);

  return (
    <>
      <TextField
        autoComplete={!props.withReqs ? "current-password" : "new-password"}
        error={props.pwErr}
        fullWidth
        label="Password"
        margin="normal"
        name="password"
        onChange={ev => {
          props.setPw(ev.target.value ?? "");
        }}
        onKeyDownCapture={ev => {
          if (ev.key === "Enter") props.submit();
        }}
        required
        sx={{ mt: 2 }}
        type="password"
        value={props.pw}
      />
      <Box sx={{ display: !props.withReqs ? "none" : "inherit" }}>
        <Typography color="text.secondary" variant="body1" sx={{ mt: 4 }}>
          Password requirements:
        </Typography>
        <List sx={{ listStyleType: "disc", pt: 0, pl: 4 }}>
          {[
            // reverse order from requirements check above
            { req: `${PWREQ_LENGTH}+ characters long`, success: pwReqLen },
            { req: `${PWREQ_NUM_LOWER}+ lowercase letters`, success: pwReqLower },
            { req: `${PWREQ_NUM_UPPER}+ capitals`, success: pwReqUpper },
            { req: `${PWREQ_NUM_DIGIT}+ digits`, success: pwReqDigit },
            { req: `${PWREQ_NUM_SPECIAL}+ special characters`, success: pwReqSpecial },
          ].map(({ req, success }) => (
            <ListItem key={req} sx={{ display: "list-item", py: 0 }}>
              <ListItemText primary={req} slotProps={{ primary: { sx: { color: success ? "#588c7e" : "#bc5a45" } } }} />
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  );
};
